/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ProductIdentifier, ApiResponse, Options, WithOriginal, WithOriginalAndMapping } from './type';
import { getSolarRecommand } from '@/service/solar-calculator';

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options: O,
    keepOriginalData: true,
): Promise<WithOriginalAndMapping<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options: O,
    keepOriginalData: false,
): Promise<WithOriginal<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options?: O,
): Promise<WithOriginal<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options?: O,
    keepOriginalData = false,
): Promise<WithOriginal<T, O>[] | WithOriginalAndMapping<T, O>[]> {
    const getAsynItem = async (productId: string, variantId: string): Promise<ApiResponse> => {
        if (!productId || !variantId) return {} as ApiResponse;
        if (productId === 'undefined' || variantId === 'undefined') return {} as ApiResponse;
        const res = await getSolarRecommand({ productId, variantId }, locale);
        if (res?.code === '0') return res.data as ApiResponse;
        throw new Error(res?.message || 'Failed to fetch');
    };

    const enrichItem = async (item: T): Promise<WithOriginal<T, O> | WithOriginalAndMapping<T, O>> => {
        try {
            const data = await getAsynItem(String(item.productId), String(item.variantId));
            const enriched: any = { ...item };

            (Object.keys(data) as (keyof ApiResponse)[]).forEach((key) => {
                const targetKey = options?.[key] || key;
                if (keepOriginalData) enriched[key] = data[key];
                // 没有拿到值就不赋值
                if (data[key] !== null && data[key] !== undefined) {
                    enriched[targetKey] = data[key];
                }
            });

            if (Array.isArray(item.children)) {
                enriched.children = await Promise.all(item.children.map(enrichItem as any));
            }

            return enriched;
        } catch (e) {
            return {
                ...item,
                children: item.children ? await Promise.all(item.children.map(enrichItem as any)) : undefined,
            } as WithOriginal<T, O>;
        }
    };

    return Promise.all(productInfo.map(enrichItem));
}

export default getProductInfo;
